import { SELECT_LIMIT } from '@src/constants/pagination';
import { useLazyGetSubAccountListQuery } from '@src/services/subAccount/subAccountList/subAccountListApiSlice';
import { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
export function useSelectAccount(
    initialSelected: { label: string; value: string }[] = []
) {
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<{ label: string; value: string }[]>(
        []
    );
    const [hasMore, setHasMore] = useState(true);
    const [selectedOptions, setSelectedOptions] = useState(initialSelected);

    const [getSubAccounts, { data, isLoading, isFetching }] =
        useLazyGetSubAccountListQuery();

    const fetchAccounts = useCallback(
        debounce(async (query: string, page: number = 1) => {
            setPage(page);
            setQuery(query);
            setHasMore(true);
            getSubAccounts({ page: page, limit: SELECT_LIMIT, search: query });
        }, 1000),
        [getSubAccounts]
    );

    useEffect(() => {
        fetchAccounts('', 1);
    }, [fetchAccounts]);

    useEffect(() => {
        if (data?.data?.items) {
            const newOptions = data.data.items.map((item) => ({
                label: item.name,
                value: item.id,
            }));

            setOptions((prev) =>
                page === 1
                    ? newOptions
                    : [
                          ...prev,
                          ...newOptions.filter(
                              (n) => !prev.some((p) => p.value === n.value)
                          ),
                      ]
            );

            const total = data.data.pagination.total;
            const loaded = page * SELECT_LIMIT;
            setHasMore(loaded < total);
        } else if (page === 1) {
            setOptions([]);
            setHasMore(false);
        }
    }, [data, page]);

    const mergedOptions = [...options];
    selectedOptions.forEach((item) => {
        if (!mergedOptions.some((o) => o.value === item.value)) {
            mergedOptions.unshift(item);
        }
    });

    const loadMore = () => {
        if (!isFetching && hasMore) {
            fetchAccounts(query, page + 1);
        }
    };

    const setSelected = (items: { label: string; value: string }[]) => {
        setSelectedOptions(items);
    };

    const resetSearch = () => {
        fetchAccounts('', 1);
    };

    return {
        options: mergedOptions,
        hasMore,
        isLoading,
        isFetching,
        selectedOptions,
        setSelected,
        resetSearch,
        loadMore,
        fetchAccounts,
    };
}
